<template>
<div>
    <h1 class="mb-4 text-gray-500">{{website}} Downloader</h1>
    <client-only>
        <template #fallback>
            <el-card shadow="never" :body-style="{padding: '0'}" class="mb-4">
                <div style="height: 100px" class="flex items-center justify-center">
                    <ElIconLoading :width="20" class="mr-2"/>
                    Loading the app
                </div>
            </el-card>
        </template>
        <downloader></downloader>
    </client-only>
    <el-card shadow="never" class="mb-4" v-if="!isLoading && downloaderInfo.id">
        <div v-html="downloaderInfo.data.content"></div>
    </el-card>

    <generic-faq :website-name="website"></generic-faq>
</div>
</template>

<script setup>
const route = useRoute()
const siteUrl = useRuntimeConfig().public.siteUrl
const {PROJECT, DOWNLOADER} = useConstants()
var downloaderInfo = reactive({
    id: '',
    data: {
        title: '',
        content: ''
    }
})
const isLoading = ref(true)
const { data } = await useCms('/content/query', {
    method: 'POST',
    body: {
        filters: {
            type: DOWNLOADER,
            project: PROJECT,
            'data.downloader': route.params.website
        }
    }
})
Object.assign(downloaderInfo, data.value?.result.items[0])
isLoading.value = false

const website = computed(() => {
    return route.params.website.split('-')
        .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ')
})

const faqStructuredData = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: `What is ${website.value} Downloader?`,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `${website.value} Downloader is a free Chrome extension that allows you to download videos from ${website.value} for offline viewing. Save your favorite content directly to your device in original quality.`
            }
        },
        {
            '@type': 'Question',
            name: `How does ${website.value} Downloader work?`,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `Our ${website.value} video downloader uses a Chrome browser extension to detect and extract video files. Simply copy the video URL, paste it into the downloader interface, and the extension will find the downloadable media for you.`
            }
        },
        {
            '@type': 'Question',
            name: `Is ${website.value} Downloader free to use?`,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `Yes, ${website.value} Downloader is completely free to use. There are no hidden fees, subscription plans, or usage limits. Simply install the Chrome extension and start downloading immediately.`
            }
        },
        {
            '@type': 'Question',
            name: 'What video quality can I download?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: `You can download ${website.value} videos in their original quality, including HD and Full HD resolutions when available. The downloader preserves the video quality as it was uploaded to the site.`
            }
        }
    ]
}))

useHead({
    title: `Free ${website.value} Downloader - Download Videos from ${website.value}`,
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: `Download videos from ${website.value} for free using our Chrome extension. Save videos in original quality for offline viewing. Fast and easy to use.`,
        },
        {
            hid: 'keywords',
            name: 'keywords',
            content: `${route.params.website} downloader, download ${route.params.website} videos, ${route.params.website} video downloader, free ${route.params.website} downloader, ${route.params.website} chrome extension`
        }
    ],
    link: [
        {rel: 'canonical', href: `${siteUrl}/${route.params.website}-downloader`}
    ],
    script: [
        {
            type: 'application/ld+json',
            children: JSON.stringify(faqStructuredData.value)
        }
    ]
})
</script>